import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { api } from '@/api/client';
import type { ConfigResponse, HealthResponse } from '@/api/types';
import { setAnonymousAuth } from '@/auth/token';
import { Button } from '@/ui/Button';
import { LogoMark, Wordmark } from '@/ui/Logo';
import { signingVerdict, type SigningVerdict } from './guard';

export interface ChainValue {
  config: ConfigResponse;
  /** Whether this tab may sign: from the cluster and genesis hash the server reports (chain/guard.ts). No browser RPC. */
  verdict: SigningVerdict;
  /** /api/health.platformId is "": the platform is not created yet, so launches and trades wait. undefined until known. */
  setupMode: boolean | undefined;
  health: HealthResponse | undefined;
}

const Ctx = createContext<ChainValue | null>(null);

/**
 * /api/config: refetched every 60 s so solUsd stays live; the last good value stays through errors. It answers 503
 * for a few seconds after a deploy, so the first load retries with backoff.
 * /api/health: the only reliable setup-mode signal. Polled every 60 s in setup mode; when the platform appears,
 * every query is refetched.
 */
export function ChainProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const cfg = useQuery({
    queryKey: ['config'],
    queryFn: api.config,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 8,
    retryDelay: (n) => Math.min(1000 * 2 ** n, 20_000),
  });
  const health = useQuery({
    queryKey: ['health'],
    queryFn: api.health,
    staleTime: 30_000,
    refetchInterval: (q) => (q.state.data?.platformId === '' ? 60_000 : 5 * 60_000),
    retry: 3,
  });
  const platformId = health.data?.platformId;
  const seen = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (platformId === undefined) return;
    if (seen.current === '' && platformId !== '') void qc.invalidateQueries();
    seen.current = platformId;
  }, [platformId, qc]);

  if (cfg.isPending) return <Boot retrying={cfg.failureCount > 0} />;
  if (cfg.isError && !cfg.data) return <Boot error={cfg.error instanceof Error ? cfg.error.message : String(cfg.error)} onRetry={() => void cfg.refetch()} />;
  return (
    <ChainInner config={cfg.data} health={health.data}>
      {children}
    </ChainInner>
  );
}

function ChainInner({ config, health, children }: { config: ConfigResponse; health: HealthResponse | undefined; children: ReactNode }) {
  const { cluster, genesisHash } = config;
  const verdict = useMemo(() => signingVerdict({ cluster, genesisHash }), [cluster, genesisHash]);
  useEffect(() => setAnonymousAuth(cluster === 'local'), [cluster]);
  const setupMode = health ? health.platformId === '' : undefined;
  const value = useMemo<ChainValue>(() => ({ config, verdict, setupMode, health }), [config, verdict, setupMode, health]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useChain(): ChainValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useChain outside ChainProvider');
  return v;
}

/** For tests and stories: provide a ready chain context. */
export const ChainContext = Ctx;

function Boot({ error, onRetry, retrying }: { error?: string; onRetry?: () => void; retrying?: boolean }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-bg px-6 text-center">
      <div className="flex items-center gap-2">
        <LogoMark size={34} />
        <Wordmark className="text-2xl" />
      </div>
      {error ? (
        <>
          <div className="text-sm font-medium text-fg">Can't reach Raydius right now</div>
          <div className="max-w-md text-xs text-fg-3">
            {error}
            {import.meta.env.DEV && (
              <>
                {' '}
                Start the server (<code className="text-fg-2">server/</code>) or the mock API (<code className="text-fg-2">npm run mock</code> in <code className="text-fg-2">web/</code>), then retry.
              </>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw className="size-3.5" /> Retry
          </Button>
        </>
      ) : (
        <div className="flex items-center gap-2 text-xs text-fg-3">
          <span className="size-1.5 animate-pulse-dot rounded-full bg-accent" /> {retrying ? 'Raydius is starting up. Retrying…' : 'Connecting to Raydius…'}
        </div>
      )}
    </div>
  );
}
